import { API_BASE, authFetch, readJson } from "./base";
import type { TodayActionResponse, TodayPlacement, TodayProjection } from "./projection-types";
import { applyFreshTodayRestartFallback, type PendingRestartCandidate } from "@/lib/todayRestartFallback";

export async function getTodayProjection(): Promise<TodayProjection> {
  // Pass user's tz so the backend can scope "today" to the user's
  // local calendar day. Mirrors the signed JS convention used by
  // `/api/schedule` (positive minutes for west-of-UTC, negative for
  // east) so a Tokyo user's evening blocks don't cross UTC midnight
  // and silently get classified as "tomorrow only".
  const tzOffsetMinutes = new Date().getTimezoneOffset();
  const response = await authFetch(
    `${API_BASE}/projections/today?tz_offset_minutes=${tzOffsetMinutes}`
  );
  const projection = await readJson<TodayProjection>(response, "Failed to load today projection");

  if (projection.restart || (projection.queue_peek?.length ?? 0) > 0) {
    return projection;
  }

  const taskResponse = await authFetch(`${API_BASE}/tasks?status=pending&limit=2`);
  if (!taskResponse.ok) {
    return projection;
  }

  const taskData = await readJson<{ tasks: PendingRestartCandidate[] }>(
    taskResponse,
    "Failed to load pending tasks for Today fallback"
  );

  return applyFreshTodayRestartFallback(projection, taskData.tasks ?? []);
}

export async function moveRestartLater(taskId: string): Promise<TodayActionResponse> {
  const response = await authFetch(`${API_BASE}/today/restart/${taskId}/later`, {
    method: "POST",
  });
  return readJson<TodayActionResponse>(response, "Failed to move restart later");
}

export async function makeRestartSmaller(
  taskId: string,
  smallerTitle: string
): Promise<TodayActionResponse> {
  const response = await authFetch(`${API_BASE}/today/restart/${taskId}/smaller`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      smaller_title: smallerTitle,
    }),
  });
  return readJson<TodayActionResponse>(response, "Failed to make restart smaller");
}

export async function getTodayPlacement(taskId: string): Promise<TodayPlacement> {
  const response = await authFetch(`${API_BASE}/today/restart/${taskId}/placement`);
  return readJson<TodayPlacement>(response, "Failed to load item placement");
}
